import { FingerprintHelper } from '@bcp-channels-sharedcomponents-reactjs/utils';
import { BaseConfig } from '@configs';
import { AppHelper, FetchHelper, FetchHelperPromiseReject, LoggerHelper, UrlHelper } from '@helpers';
import { useSearchParamsStore, useServicesStore } from '@store';
import {
	AccountCmdUrlInputDto,
	AccountCmdUrlOutputDtoSingleOutputMessage,
	AccountDeleteCookiesInputDto,
	AccountDeleteCookiesOutputDtoSingleOutputMessage,
	AccountGenerateCaptchaInputDto,
	AccountGenerateCaptchaOutputDtoSingleOutputMessage,
	AccountLoginInputDto,
	AccountLoginOutputDtoSingleOutputMessage,
	AccountSafeInputDto,
	AccountSafeOutputDtoSingleOutputMessage,
	AccountUserInputDto,
	AccountUserOutputDtoSingleOutputMessage,
	AnalyticsPSD2InputDto,
	AnalyticsPSD2OutputDtoSingleOutputMessage,
	ContentsClientCookiesInputDto,
	ContentsClientCookiesOutputDtoSingleOutputMessage,
	ContentsClientInputDto,
	ContentsClientOutputDtoSingleOutputMessage,
	endpoints,
} from '@types';

const fingerprint = FingerprintHelper.getFingerprint();

export const useApi = () => {
	const { responseType, clientId, redirectUri, codeChallenge, codeChallengeMethod, scope, state, language } =
		useSearchParamsStore();
	const {
		userAlias,
		userAliasTypeId,
		sdcBusinessSessionId,
		challengeType,
		sdcSessionId,
		safeCredentialType,
		geo,
		targetSystem,
		targetInfrastructure,
	} = useServicesStore((state) => state.accountLogin) || {};

	const environmentReturnUrl = BaseConfig.environmentReturnUrl ? `/${BaseConfig.environmentReturnUrl}` : '';
	const defaults = {
		bank: 0,
		returnUrl: UrlHelper.buildUrl(`${environmentReturnUrl}/connect/authorize`, [
			{ key: 'response_type', value: responseType },
			{ key: 'client_id', value: clientId },
			{ key: 'redirect_uri', value: redirectUri, encode: true },
			{ key: 'code_challenge', value: codeChallenge },
			{ key: 'code_challenge_method', value: codeChallengeMethod },
			{ key: 'scope', value: scope, encode: true },
			{ key: 'state', value: state },
		]),
		fingerprint,
		userAlias,
		userAliasTypeId,
		sdcBusinessSessionId,
		challengeType,
		sdcSessionId,
		language,
		safeCredentialType,
		geo,
		targetSystem,
		targetInfrastructure,
	};

	const accountGenerateCaptcha = async (): Promise<AccountGenerateCaptchaOutputDtoSingleOutputMessage> => {
		const accountGenerateCaptchaInput: AccountGenerateCaptchaInputDto = {
			userAlias: defaults.userAlias,
			returnUrl: defaults.returnUrl,
		};

		return middleware('post', endpoints.accountGenerateCaptcha, accountGenerateCaptchaInput, (output) => {
			LoggerHelper.log('Api', 'useApi > accountGenerateCaptcha [out]:', {
				input: accountGenerateCaptchaInput,
				output,
			});

			return output;
		});
	};

	const accountLogin = async (input?: AccountLoginInputDto): Promise<AccountLoginOutputDtoSingleOutputMessage> => {
		const accountLoginInput: AccountLoginInputDto = {
			bank: defaults.bank,
			returnUrl: defaults.returnUrl,
			fingerprint: await defaults.fingerprint,
			...input,
		};

		return middleware('post', endpoints.accountLogin, accountLoginInput, (output) => {
			LoggerHelper.log('Api', 'useApi > accountLogin [out]:', {
				input: accountLoginInput,
				output,
			});

			return output;
		});
	};

	const accountSafe = async (input?: AccountSafeInputDto): Promise<AccountSafeOutputDtoSingleOutputMessage> => {
		const accountSafeInput: AccountSafeInputDto = {
			sdcBusinessSessionId: defaults.sdcBusinessSessionId,
			sdcSessionId: defaults.sdcSessionId,
			returnUrl: defaults.returnUrl,
			userAlias: defaults.userAlias,
			userAliasTypeId: defaults.userAliasTypeId,
			credentialTypeId: input?.credentialTypeId,
			geo: defaults.geo,
			targetSystem: defaults.targetSystem,
			targetInfrastructure: defaults.targetInfrastructure,
		};

		return middleware('post', endpoints.accountSafe, accountSafeInput, (output) => {
			LoggerHelper.log('Api', 'useApi > accountSafe [out]:', {
				input: accountSafeInput,
				output,
			});

			return output;
		});
	};

	const accountDeleteCookies = async (
		input?: AccountDeleteCookiesInputDto
	): Promise<AccountDeleteCookiesOutputDtoSingleOutputMessage> => {
		const urlInput = endpoints.accountDeleteCookies;

		return middleware('get', urlInput, undefined, (output) => {
			LoggerHelper.log('Api', 'useApi > accountDeleteCookies [out]:', {
				input: urlInput,
				output,
			});

			return output;
		});
	};

	const accountUser = async (input?: AccountUserInputDto): Promise<AccountUserOutputDtoSingleOutputMessage> => {
		const accountUserInput: AccountUserInputDto = {
			k: input?.k,
			sf: input?.sf,
			fingerprint: await defaults.fingerprint,
		};

		return middleware('post', endpoints.accountUser, accountUserInput, (output) => {
			LoggerHelper.log('Api', 'useApi > accountUser [out]:', {
				input: accountUserInput,
				output,
			});

			return output;
		});
	};

	const accountCmdUrl = async (input?: AccountCmdUrlInputDto): Promise<AccountCmdUrlOutputDtoSingleOutputMessage> => {
		const accountCmdUrlInput: AccountCmdUrlInputDto = {
			bank: defaults.bank,
			returnUrl: defaults.returnUrl,
			fingerprint: await defaults.fingerprint,
			...input,
		};

		return middleware('post', endpoints.accountCmdUrl, accountCmdUrlInput, (output) => {
			LoggerHelper.log('Api', 'useApi > accountCmdUrl [out]:', {
				input: accountCmdUrlInput,
				output,
			});

			return output;
		});
	};

	const analyticsPSD2 = async (input?: AnalyticsPSD2InputDto): Promise<AnalyticsPSD2OutputDtoSingleOutputMessage> => {
		const analyticsPSD2Input: AnalyticsPSD2InputDto = {
			returnUrl: defaults.returnUrl,
			hasChallenge: input?.hasChallenge,
		};

		return middleware('post', endpoints.analyticsPSD2, analyticsPSD2Input, (output) => {
			LoggerHelper.log('Api', 'useApi > analyticsPSD2 [out]:', {
				input: analyticsPSD2Input,
				output,
			});

			return output;
		});
	};

	const contentsClient = async (
		input?: ContentsClientInputDto
	): Promise<ContentsClientOutputDtoSingleOutputMessage> => {
		const contentsClientInput: ContentsClientInputDto = {
			clientId: input?.clientId,
			language: input?.language || defaults.language,
		};

		return middleware('post', endpoints.contentsClient, contentsClientInput, (output) => {
			LoggerHelper.log('Api', 'useApi > contentsClient [out]:', {
				input,
				output,
			});

			return output;
		});
	};

	const contentsClientCookies = async (
		input?: ContentsClientCookiesInputDto
	): Promise<ContentsClientCookiesOutputDtoSingleOutputMessage> => {
		const contentsClientCookiesInput: ContentsClientCookiesInputDto = {
			clientId: input?.clientId,
			language: input?.language || defaults.language,
		};

		return middleware('post', endpoints.contentsClientCookies, contentsClientCookiesInput, (output) => {
			LoggerHelper.log('Api', 'useApi > contentsClientCookies [out]:', {
				input,
				output,
			});

			return output;
		});
	};

	const middleware = async <TInput, TOutput>(
		method: 'post' | 'get',
		endpoint: string,
		input: TInput | undefined,
		callback: (output: TOutput) => TOutput
	): Promise<TOutput> => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const output: any = await FetchHelper[method](endpoint, input);

			if (AppHelper.isSessionExpired(output?.errorCode)) {
				return Promise.reject({
					metadata: {
						success: false,
						input: input,
						errors: [
							{
								code: output?.errorCode,
								message: output?.errorMessage,
							},
						],
					},
					data: output,
				} as TOutput);
			}

			return callback({
				metadata: {
					success: true,
					input: input,
				},
				data: output,
			} as TOutput);
		} catch (err) {
			const fetchHelperError = err as FetchHelperPromiseReject | undefined;

			return Promise.reject({
				metadata: {
					success: false,
					input: input,
					errors: [
						{
							code: fetchHelperError?.metadata?.error?.code?.toString(),
							message: fetchHelperError?.metadata?.error?.message?.toString(),
						},
					],
				},
				data: fetchHelperError?.data,
			} as TOutput);
		}
	};

	return {
		accountGenerateCaptcha,
		accountLogin,
		accountSafe,
		accountDeleteCookies,
		accountUser,
		accountCmdUrl,
		analyticsPSD2,
		contentsClient,
		contentsClientCookies,
	};
};
